import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useClickOutside } from '../../app/hooks/useClickOutside';
import { ChevronDown, Check } from 'lucide-react';
import { Account, AccountRole } from '../types';
import { getAccountIcon, getAccountRoleLabel, ACCOUNT_ROLE_COLORS } from '../logic/uiHelpers';
interface AccountSwitcherDropdownProps {
  accounts: Account[];
  activeAccountId: string;
  onSwitchAccount: (accountId: string) => void;
  isDemoMode?: boolean;
}
export function AccountSwitcherDropdown({
  accounts,
  activeAccountId,
  onSwitchAccount,
  isDemoMode = true
}: AccountSwitcherDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const activeAccount = accounts.find(a => a.id === activeAccountId);
  // Close dropdown when clicking outside
  useClickOutside(dropdownRef, () => setIsOpen(false), isOpen);
  if (!activeAccount) return null;
  const Icon = getAccountIcon(activeAccount.role);
  const colors = ACCOUNT_ROLE_COLORS[activeAccount.role];
  // Group accounts by role
  const groupedAccounts: Record<AccountRole, Account[]> = {
    learner: accounts.filter(a => a.role === 'learner'),
    parent: accounts.filter(a => a.role === 'parent'),
    teacher: accounts.filter(a => a.role === 'teacher'),
    admin: accounts.filter(a => a.role === 'admin')
  };
  return <div className="relative" ref={dropdownRef} data-id="element-12">
      {/* Trigger Button */}
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-orange-400" data-id="element-13">
        
        {/* Avatar */}
        <div className={`w-8 h-8 rounded-full ${colors.bg} flex items-center justify-center`} data-id="element-14">
          
          <Icon className={`w-4 h-4 ${colors.text}`} strokeWidth={2.5} data-id="element-15" />
        </div>

        {/* Name & Role (hidden on mobile) */}
        <div className="hidden md:block text-left" data-id="element-16">
          <div className="text-sm font-bold text-slate-900 leading-tight" data-id="element-17">
            {activeAccount.displayName}
          </div>
          <div className="text-xs text-slate-500" data-id="element-18">
            {getAccountRoleLabel(activeAccount.role)}
          </div>
        </div>

        {/* Chevron */}
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-19" />
        
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence data-id="element-20">
        {isOpen && <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: -10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: -10
      }} transition={{
        duration: 0.15,
        ease: 'easeOut'
      }} className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50 max-h-[80vh] overflow-y-auto" data-id="element-21">
          
            <div className="p-2" data-id="element-22">
              <div className="px-3 py-2 text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 mb-2" data-id="element-23">
                {isDemoMode ? 'Demo Account Wisselen' : 'Account Wisselen'}
              </div>

              {(['learner', 'parent', 'teacher', 'admin'] as AccountRole[]).map(role => {
            const roleAccounts = groupedAccounts[role];
            if (roleAccounts.length === 0) return null;
            return <div key={role} className="mb-3 last:mb-0" data-id="element-24">
                      <div className="px-3 py-1 text-[10px] font-black text-slate-400 uppercase tracking-wider" data-id="element-25">
                        {getAccountRoleLabel(role)}
                      </div>

                      {roleAccounts.map(account => {
                const isActive = account.id === activeAccountId;
                const AccountIcon = getAccountIcon(account.role);
                const accountColors = ACCOUNT_ROLE_COLORS[account.role];
                return <button key={account.id} onClick={() => {
                  onSwitchAccount(account.id);
                  setIsOpen(false);
                }} className={`
                            w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all mt-0.5
                            ${isActive ? `${accountColors.bg} ${accountColors.border} border` : 'hover:bg-slate-50'}
                          `} data-id="element-26">
                          
                            {/* Avatar */}
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${accountColors.bg}`} data-id="element-27">
                            
                              <AccountIcon className={`w-4 h-4 ${accountColors.text}`} strokeWidth={2.5} data-id="element-28" />
                            
                            </div>

                            {/* Name */}
                            <div className="flex-1 text-left min-w-0" data-id="element-29">
                              <div className="font-bold text-sm text-slate-900 truncate" data-id="element-30">
                                {account.displayName}
                              </div>
                            </div>

                            {/* Active Indicator */}
                            {isActive && <motion.div initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 25
                  }} data-id="element-31">
                            
                                <Check className={`w-4 h-4 ${accountColors.text}`} strokeWidth={3} data-id="element-32" />
                            
                              </motion.div>}
                          </button>;
              })}
                    </div>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}